import axios from 'axios';
import { createAsyncThunk, createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { API_ROUTES } from '@/config/api-routes';
import { resolveCrmProfileUsername } from '@/shared/auth/auth.reducer';
import { buildLeadCreateBody, buildLeadUpdateBody, computeAgentLeadStatistics, mapCampaignCustomerToLead, mapLeadDtoToLead, defaultValue, } from '../model/campaign.model';
import { appendCriteriaContains, appendCriteriaEquals, appendCriteriaGreaterThanOrEqual, appendCriteriaIn, appendCriteriaLessThanOrEqual, appendPageable, readPagedRows, } from '../utils/spring-api';
import { serializeAxiosError, resolveAsyncThunkErrorMessage } from './reducer.utils';
const campaignsUrl = API_ROUTES.campaigns;
const leadsUrl = API_ROUTES.leads;
const usersUrl = API_ROUTES.users;
const callsAttemptsUrl = API_ROUTES.callsAttempts;
const initialState = {
    loading: false,
    listLoading: false,
    detailLoading: false,
    leadsLoading: false,
    statsLoading: false,
    assignmentPoolLoading: false,
    assignableAgentsLoading: false,
    mutationLoading: false,
    errorMessage: null,
    campaign: defaultValue,
    campaigns: [],
    totalCampaigns: 0,
    campaignDetail: null,
    leads: [],
    totalLeads: 0,
    assignmentPoolLeads: [],
    assignmentPoolTotal: 0,
    assignableAgents: [],
    agentLeadStatistics: [],
    leadCallAttempts: [],
    leadCallAttemptsLoading: false,
    leadAttemptCounts: {},
    updating: false,
    updateSuccess: false,
};
async function fetchAllCampaignCustomers(campaignId) {
    const pageSize = 500;
    let page = 0;
    const all = [];
    for (;;) {
        const q = new URLSearchParams();
        appendPageable(q, page, pageSize, 'id,desc');
        const response = await axios.get(`${API_ROUTES.campaignCustomers(campaignId)}?${q.toString()}`);
        const { rows, total } = readPagedRows(response.data, response.headers);
        all.push(...rows.map((row) => mapCampaignCustomerToLead(row, campaignId)));
        if (all.length >= total || rows.length === 0) {
            break;
        }
        page += 1;
    }
    return all;
}
async function fetchExistingMobileNumbers(campaignId, numbers) {
    const existing = new Set();
    const chunkSize = 50;
    for (let i = 0; i < numbers.length; i += chunkSize) {
        const chunk = numbers.slice(i, i + chunkSize);
        const q = new URLSearchParams();
        appendPageable(q, 0, chunkSize, 'id,asc');
        appendCriteriaEquals(q, 'campaignId', campaignId);
        appendCriteriaIn(q, 'mobileNumber', chunk);
        const response = await axios.get(`${leadsUrl}?${q.toString()}`);
        const { rows } = readPagedRows(response.data, response.headers);
        for (const row of rows) {
            if (row.mobileNumber) {
                existing.add(row.mobileNumber);
            }
        }
    }
    return existing;
}
export const fetchCampaigns = createAsyncThunk('campaignManagement/fetch_campaigns', async (params) => {
    const { page = 0, size = 10, sort = 'name,asc', name, status, startDate, endDate, } = params;
    const q = new URLSearchParams();
    appendPageable(q, page, size, sort);
    appendCriteriaContains(q, 'name', name);
    appendCriteriaEquals(q, 'status', status);
    appendCriteriaGreaterThanOrEqual(q, 'startDate', startDate ?? undefined);
    appendCriteriaLessThanOrEqual(q, 'endDate', endDate ?? undefined);
    const requestUrl = `${campaignsUrl}?${q.toString()}`;
    return axios.get(requestUrl);
}, { serializeError: serializeAxiosError });
export const fetchCampaignById = createAsyncThunk('campaignManagement/fetch_campaign', async (id) => {
    const requestUrl = `${campaignsUrl}/${id}`;
    return axios.get(requestUrl);
}, { serializeError: serializeAxiosError });
/** Paginated campaign customers — `GET /api/campaigns/{id}/customers` */
export const fetchCampaignLeads = createAsyncThunk('campaignManagement/fetch_campaign_leads', async (params) => {
    const { campaignId, page = 0, size = 10, sort = 'id,desc', customerName, phone, status, } = params;
    const q = new URLSearchParams();
    appendPageable(q, page, size, sort);
    if (customerName?.trim()) {
        q.set('customerName', customerName.trim());
    }
    if (phone?.trim()) {
        q.set('mobileNumber', phone.trim());
    }
    if (status) {
        q.set('status', status);
    }
    const requestUrl = `${API_ROUTES.campaignCustomers(campaignId)}?${q.toString()}`;
    const response = await axios.get(requestUrl);
    const { rows, total } = readPagedRows(response.data, response.headers);
    return {
        ...response,
        data: rows.map((row) => mapCampaignCustomerToLead(row, campaignId)),
        total,
    };
}, { serializeError: serializeAxiosError });
/** Unassigned pool — `GET /api/leads?campaignId.equals&status.equals=NEW` */
export const fetchAssignmentPoolLeads = createAsyncThunk('campaignManagement/fetch_assignment_pool_leads', async (params) => {
    const { campaignId, page = 0, size = 50 } = params;
    const q = new URLSearchParams();
    appendPageable(q, page, size, 'id,desc');
    appendCriteriaEquals(q, 'campaignId', campaignId);
    appendCriteriaEquals(q, 'status', 'NEW');
    const response = await axios.get(`${leadsUrl}?${q.toString()}`);
    const { rows, total } = readPagedRows(response.data, response.headers);
    return {
        ...response,
        data: rows.map((row) => mapLeadDtoToLead(row, campaignId)),
        total,
    };
}, { serializeError: serializeAxiosError });
/** Active agents — `GET /api/users?userType.equals=AGENT&status.equals=ACTIVE` */
export const fetchAssignableAgents = createAsyncThunk('campaignManagement/fetch_assignable_agents', async (_campaignId) => {
    const q = new URLSearchParams();
    appendPageable(q, 0, 1000, 'name,asc');
    appendCriteriaEquals(q, 'userType', 'AGENT');
    appendCriteriaEquals(q, 'status', 'ACTIVE');
    const response = await axios.get(`${usersUrl}?${q.toString()}`);
    const { rows } = readPagedRows(response.data, response.headers);
    const agents = rows
        .filter((user) => user.username?.trim())
        .map((user) => ({
        username: user.username.trim(),
        displayName: user.name?.trim() ? user.name.trim() : user.username.trim(),
    }));
    return { ...response, data: agents };
}, { serializeError: serializeAxiosError });
/** `POST /api/campaigns/{id}/assign` */
export const batchAssignCampaignLeads = createAsyncThunk('campaignManagement/batch_assign_leads', async ({ campaignId, agentUsername, leadIds }) => {
    const numericIds = leadIds.map((id) => Number(id)).filter((id) => Number.isFinite(id));
    const result = await axios.post(API_ROUTES.campaignAssign(campaignId), {
        agentUsername,
        leadIds: numericIds,
    });
    return result;
}, { serializeError: serializeAxiosError });
/** Derived client-side from all campaign customers (no dedicated API). */
export const fetchAgentLeadStatistics = createAsyncThunk('campaignManagement/fetch_agent_lead_statistics', async (campaignId) => {
    const leads = await fetchAllCampaignCustomers(campaignId);
    return computeAgentLeadStatistics(leads);
}, { serializeError: serializeAxiosError });
export const createCampaign = createAsyncThunk('campaignManagement/create_campaign', async (entity) => {
    const result = await axios.post(campaignsUrl, entity);
    return result;
}, { serializeError: serializeAxiosError });
export const updateCampaign = createAsyncThunk('campaignManagement/update_campaign', async (entity) => {
    const result = await axios.put(`${campaignsUrl}/${entity.id}`, entity);
    return result;
}, { serializeError: serializeAxiosError });
/** `POST /api/leads` */
export const addCampaignLeadManual = createAsyncThunk('campaignManagement/add_lead_manual', async (payload) => {
    const { campaignId, ...values } = payload;
    const body = buildLeadCreateBody(campaignId, values);
    const result = await axios.post(leadsUrl, body);
    return {
        ...result,
        data: mapLeadDtoToLead(result.data, campaignId),
    };
}, { serializeError: serializeAxiosError });
/** `DELETE /api/leads/{id}` */
export const deleteCampaignLead = createAsyncThunk('campaignManagement/delete_lead', async ({ campaignId, leadId }) => {
    await axios.delete(`${leadsUrl}/${leadId}`);
    return { campaignId, leadId };
}, { serializeError: serializeAxiosError });
/** `PUT /api/leads/{id}` */
export const updateCampaignLead = createAsyncThunk('campaignManagement/update_lead', async ({ campaignId, lead, values }) => {
    const body = buildLeadUpdateBody(lead, campaignId, values);
    const result = await axios.put(`${leadsUrl}/${lead.id}`, body);
    return {
        ...result,
        data: mapLeadDtoToLead(result.data, campaignId),
    };
}, { serializeError: serializeAxiosError });
/** Client-side preview with duplicate check via `/api/leads` */
export const previewImportLeads = createAsyncThunk('campaignManagement/preview_import_leads', async ({ campaignId, rows }) => {
    const numbers = rows.map((row) => row.number).filter(Boolean);
    const existingNumbers = numbers.length > 0 ? await fetchExistingMobileNumbers(campaignId, numbers) : new Set();
    const seen = new Set();
    const data = rows.map((row) => {
        const duplicateInCampaign = existingNumbers.has(row.number);
        const duplicateInFile = seen.has(row.number);
        if (row.number) {
            seen.add(row.number);
        }
        const duplicate = duplicateInCampaign || duplicateInFile;
        return {
            ...row,
            duplicate,
            correct: Boolean(row.correct) && !duplicate,
        };
    });
    return { data };
}, { serializeError: serializeAxiosError });
/** Import valid rows via repeated `POST /api/leads` */
export const commitImportLeads = createAsyncThunk('campaignManagement/commit_import_leads', async ({ campaignId, rows }) => {
    const validRows = rows.filter((row) => row.correct && !row.duplicate);
    const created = [];
    for (const row of validRows) {
        const body = buildLeadCreateBody(campaignId, {
            name: row.name,
            number: row.number,
            type: row.type,
            service: row.service,
            language: row.language,
        });
        const result = await axios.post(leadsUrl, body);
        created.push(mapLeadDtoToLead(result.data, campaignId));
    }
    return { data: created };
}, { serializeError: serializeAxiosError });
/** `GET /api/calls/attempts?leadId.equals={id}` */
export const fetchLeadCallAttempts = createAsyncThunk('campaignManagement/fetch_lead_call_attempts', async ({ leadId, page = 0, size = 100 }) => {
    const q = new URLSearchParams();
    appendPageable(q, page, size, 'startedAt,desc');
    appendCriteriaEquals(q, 'leadId', leadId);
    const response = await axios.get(`${callsAttemptsUrl}?${q.toString()}`);
    const { rows, total } = readPagedRows(response.data, response.headers);
    return { leadId, rows, total };
}, { serializeError: serializeAxiosError });
/** Batch attempt totals for visible leads — `GET /api/calls/attempts?leadId.equals` */
export const fetchLeadAttemptCounts = createAsyncThunk('campaignManagement/fetch_lead_attempt_counts', async (leadIds) => {
    const uniqueIds = [...new Set(leadIds.filter(Boolean))];
    const counts = {};
    await Promise.all(uniqueIds.map(async (leadId) => {
        const q = new URLSearchParams();
        appendPageable(q, 0, 1, 'id,desc');
        appendCriteriaEquals(q, 'leadId', leadId);
        const response = await axios.get(`${callsAttemptsUrl}?${q.toString()}`);
        const { total } = readPagedRows(response.data, response.headers);
        counts[leadId] = total;
    }));
    return counts;
}, { serializeError: serializeAxiosError });
/** `POST /api/calls/attempts` */
export const submitLeadCallOutcome = createAsyncThunk('campaignManagement/submit_call_outcome', async ({ leadId, outcome, notes, callSessionId }, { getState }) => {
    const state = getState();
    const agentUsername = resolveCrmProfileUsername(state.auth.crmProfile) ?? state.auth.session?.username;
    if (!agentUsername) {
        throw new Error('Current user is not available for call outcome.');
    }
    const sessionId = callSessionId?.trim();
    const result = await axios.post(callsAttemptsUrl, {
        lead: { id: Number(leadId) },
        agent: { username: agentUsername },
        outcome,
        note: notes?.trim() || undefined,
        ...(sessionId ? { callSessionId: sessionId } : {}),
    });
    return result;
}, { serializeError: serializeAxiosError });
export const CampaignManagementSlice = createSlice({
    name: 'campaignManagement',
    initialState: initialState,
    reducers: {
        reset() {
            return { ...initialState };
        },
        clearCampaignDetail(state) {
            state.campaignDetail = null;
            state.leads = [];
            state.totalLeads = 0;
            state.assignmentPoolLeads = [];
            state.assignmentPoolTotal = 0;
            state.assignableAgents = [];
            state.agentLeadStatistics = [];
            state.errorMessage = null;
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchCampaigns.fulfilled, (state, action) => {
            state.listLoading = false;
            state.campaigns = action.payload.data;
            const raw = action.payload.headers['x-total-count'];
            state.totalCampaigns = raw ? parseInt(String(raw), 10) : action.payload.data.length;
        })
            .addCase(fetchCampaignById.fulfilled, (state, action) => {
            state.detailLoading = false;
            state.campaignDetail = action.payload.data;
        })
            .addCase(fetchCampaignLeads.fulfilled, (state, action) => {
            state.leadsLoading = false;
            state.leads = action.payload.data;
            state.totalLeads = action.payload.total;
        })
            .addCase(fetchAssignmentPoolLeads.fulfilled, (state, action) => {
            state.assignmentPoolLoading = false;
            state.assignmentPoolLeads = action.payload.data;
            state.assignmentPoolTotal = action.payload.total;
        })
            .addCase(fetchAssignableAgents.fulfilled, (state, action) => {
            state.assignableAgentsLoading = false;
            state.assignableAgents = action.payload.data;
        })
            .addCase(batchAssignCampaignLeads.fulfilled, (state) => {
            state.mutationLoading = false;
        })
            .addCase(fetchAgentLeadStatistics.fulfilled, (state, action) => {
            state.statsLoading = false;
            state.agentLeadStatistics = action.payload;
        })
            .addCase(fetchLeadCallAttempts.fulfilled, (state, action) => {
            state.leadCallAttemptsLoading = false;
            state.leadCallAttempts = action.payload.rows;
            state.leadAttemptCounts[action.payload.leadId] = action.payload.total;
        })
            .addCase(fetchLeadAttemptCounts.fulfilled, (state, action) => {
            state.leadAttemptCounts = { ...state.leadAttemptCounts, ...action.payload };
        })
            .addCase(createCampaign.fulfilled, (state, action) => {
            state.updating = false;
            state.loading = false;
            state.updateSuccess = true;
            state.campaign = action.payload.data;
        })
            .addCase(updateCampaign.fulfilled, (state, action) => {
            state.updating = false;
            state.loading = false;
            state.updateSuccess = true;
            state.campaign = action.payload.data;
            const updated = action.payload.data;
            const idx = state.campaigns.findIndex((c) => c.id === updated.id);
            if (idx >= 0) {
                state.campaigns[idx] = updated;
            }
            if (state.campaignDetail?.id === updated.id) {
                state.campaignDetail = updated;
            }
        })
            .addCase(addCampaignLeadManual.fulfilled, (state) => {
            state.mutationLoading = false;
        })
            .addCase(deleteCampaignLead.fulfilled, (state) => {
            state.mutationLoading = false;
        })
            .addCase(updateCampaignLead.fulfilled, (state, action) => {
            state.mutationLoading = false;
            const updated = action.payload.data;
            const idx = state.leads.findIndex((lead) => lead.id === updated.id);
            if (idx >= 0) {
                state.leads = state.leads.map((lead, i) => (i === idx ? updated : lead));
            }
        })
            .addCase(commitImportLeads.fulfilled, (state) => {
            state.mutationLoading = false;
        })
            .addCase(submitLeadCallOutcome.fulfilled, (state) => {
            state.mutationLoading = false;
        })
            .addMatcher(isPending(fetchCampaigns), (state) => {
            state.errorMessage = null;
            state.listLoading = true;
        })
            .addMatcher(isPending(fetchCampaignById), (state) => {
            state.errorMessage = null;
            state.detailLoading = true;
        })
            .addMatcher(isPending(fetchCampaignLeads), (state) => {
            state.errorMessage = null;
            state.leadsLoading = true;
        })
            .addMatcher(isPending(fetchAssignmentPoolLeads), (state) => {
            state.errorMessage = null;
            state.assignmentPoolLoading = true;
        })
            .addMatcher(isPending(fetchAssignableAgents), (state) => {
            state.errorMessage = null;
            state.assignableAgentsLoading = true;
        })
            .addMatcher(isPending(fetchAgentLeadStatistics), (state) => {
            state.errorMessage = null;
            state.statsLoading = true;
        })
            .addMatcher(isPending(fetchLeadCallAttempts), (state) => {
            state.errorMessage = null;
            state.leadCallAttemptsLoading = true;
        })
            .addMatcher(isPending(createCampaign, updateCampaign), (state) => {
            state.errorMessage = null;
            state.updateSuccess = false;
            state.updating = true;
        })
            .addMatcher(isPending(addCampaignLeadManual, deleteCampaignLead, updateCampaignLead, commitImportLeads, submitLeadCallOutcome, batchAssignCampaignLeads), (state) => {
            state.errorMessage = null;
            state.mutationLoading = true;
        })
            .addMatcher(isRejected(fetchCampaigns), (state, action) => {
            state.listLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(fetchCampaignById), (state, action) => {
            state.detailLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(fetchCampaignLeads), (state, action) => {
            state.leadsLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(fetchAssignmentPoolLeads), (state, action) => {
            state.assignmentPoolLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(fetchAssignableAgents), (state, action) => {
            state.assignableAgentsLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(fetchAgentLeadStatistics), (state, action) => {
            state.statsLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(fetchLeadCallAttempts), (state, action) => {
            state.leadCallAttemptsLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(fetchLeadAttemptCounts), (state, action) => {
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(createCampaign, updateCampaign), (state, action) => {
            state.loading = false;
            state.updating = false;
            state.updateSuccess = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        })
            .addMatcher(isRejected(addCampaignLeadManual, deleteCampaignLead, updateCampaignLead, commitImportLeads, submitLeadCallOutcome, batchAssignCampaignLeads), (state, action) => {
            state.mutationLoading = false;
            state.errorMessage = resolveAsyncThunkErrorMessage(action.error);
        });
    },
});
export const { reset, clearCampaignDetail } = CampaignManagementSlice.actions;
export default CampaignManagementSlice.reducer;
